import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import {
  Dialog,
  Input,
  OneModal,
  Spinner,
} from '@factorialco/factorial-one-react/dist/experimental'
import { Delete } from '@factorialco/factorial-one-react/icons/app'
import {
  MeetingBaseFragment,
  MeetingsAllMeetingsTableDocument,
  MeetingsDeleteMeetingDocument,
  MeetingsUpdateMeetingDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface EditMeetingModalProps {
  openEditModal: boolean
  setOpenEditModal: (open: boolean) => void
  meeting: MeetingBaseFragment
}

export const EditMeetingModal = ({
  openEditModal,
  setOpenEditModal,
  meeting,
}: EditMeetingModalProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [title, setTitle] = useState(meeting?.title || '')
  const [date, setDate] = useState(moment(meeting?.date).format('YYYY-MM-DD'))
  const [startTime, setStartTime] = useState(moment(meeting?.startTime).local().format('HH:mm'))
  const [endTime, setEndTime] = useState(moment(meeting?.endTime).local().format('HH:mm'))

  const [isLoadingDeleteMeeting, setIsLoadingDeleteMeeting] = useState(false)
  const [isLoadingUpdateMeeting, setIsLoadingUpdateMeeting] = useState(false)

  const [deleteMeeting] = useMutation(MeetingsDeleteMeetingDocument, {
    extractorFn: (data) => data.meetingsMutations.deleteMeetingsMeeting,
    refetchQueries: [MeetingsAllMeetingsTableDocument],
  })

  const [updateMeeting] = useMutation(MeetingsUpdateMeetingDocument, {
    extractorFn: (data) => data.meetingsMutations.updateMeetingsMeeting,
  })

  const handleDeleteMeeting = async () => {
    if (!meeting?.id) return

    setIsLoadingDeleteMeeting(true)
    const result = await deleteMeeting({ variables: { id: meeting.id } })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('meetings.meeting.edit_meeting.delete_meeting_success'),
      })
      // TODO: change this with a hook that retrives the meeting type
      navigate(navigation.engagement.one_on_ones.fullPath())
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
    setIsLoadingDeleteMeeting(false)
  }

  const canUpdateMeeting = title.length > 2 && date && startTime && endTime && startTime < endTime

  const handleUpdateMeeting = async () => {
    if (!meeting?.id || !canUpdateMeeting) return

    setIsLoadingUpdateMeeting(true)
    const result = await updateMeeting({
      variables: {
        id: meeting.id,
        title: title,
        date: moment(date).format('YYYY-MM-DD'),
        startTime: moment(`${date} ${startTime}`).utc().toISOString(),
        endTime: moment(`${date} ${endTime}`).utc().toISOString(),
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('meetings.meeting.edit_meeting.update_meeting_success'),
      })
      setOpenEditModal(false)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
    setIsLoadingUpdateMeeting(false)
  }

  return (
    <>
      <OneModal isOpen={openEditModal} onClose={() => setOpenEditModal(false)}>
        {isLoadingDeleteMeeting || isLoadingUpdateMeeting ? (
          <OneModal.Content>
            <Box
              paddingX="s16"
              paddingBottom="s64"
              paddingTop="s64"
              gap="s12"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner />
              <Text.Normal size="200">
                {isLoadingDeleteMeeting
                  ? i18n.t('meetings.meeting.edit_meeting.delete_meeting_loading')
                  : i18n.t('meetings.meeting.edit_meeting.update_meeting_loading')}
              </Text.Normal>
            </Box>
          </OneModal.Content>
        ) : (
          <>
            <OneModal.Header
              title={i18n.t('meetings.meeting.edit_meeting.title')}
              otherActions={[
                {
                  icon: Delete,
                  label: i18n.t('meetings.meeting.edit_meeting.delete_meeting'),
                  onClick: () => setOpenDeleteDialog(true),
                },
              ]}
            />
            <OneModal.Content>
              <Box paddingX="s16" paddingBottom="s12" gap="s12">
                <Box gap="s8">
                  <Text.Normal size="200">
                    {i18n.t('meetings.meeting.edit_meeting.title_input')}
                  </Text.Normal>
                  <Input
                    placeholder={i18n.t('meetings.meeting.edit_meeting.title_input_placeholder')}
                    type="text"
                    value={title || ''}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </Box>
                <Box gap="s8">
                  <Text.Normal size="200">
                    {i18n.t('meetings.meeting.edit_meeting.date_input')}
                  </Text.Normal>
                  <Input
                    placeholder={i18n.t('meetings.meeting.edit_meeting.date_input_placeholder')}
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Box>

                <Box gap="s12" flexDirection="row">
                  <Box gap="s8" flexGrow>
                    <Text.Normal size="200">
                      {i18n.t('meetings.meeting.edit_meeting.start_time_input')}
                    </Text.Normal>
                    <Input
                      placeholder={i18n.t(
                        'meetings.meeting.edit_meeting.start_time_input_placeholder'
                      )}
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </Box>
                  <Box gap="s8" flexGrow>
                    <Text.Normal size="200">
                      {i18n.t('meetings.meeting.edit_meeting.end_time_input')}
                    </Text.Normal>
                    <Input
                      placeholder={i18n.t(
                        'meetings.meeting.edit_meeting.end_time_input_placeholder'
                      )}
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </Box>
                </Box>
              </Box>
            </OneModal.Content>
            <OneModal.Footer>
              <Button
                onClick={() => setOpenEditModal(false)}
                label={i18n.t('common.cancel')}
                variant="outline"
              />
              <Button
                disabled={!canUpdateMeeting}
                onClick={handleUpdateMeeting}
                label={i18n.t('meetings.meeting.edit_meeting.save_meeting')}
                variant="default"
                isLoading={isLoadingUpdateMeeting}
              />
            </OneModal.Footer>
          </>
        )}
      </OneModal>
      <Dialog
        onClose={() => setOpenDeleteDialog(false)}
        actions={{
          primary: {
            icon: Delete,
            label: i18n.t('meetings.meeting.edit_meeting.delete_meeting'),
            onClick: handleDeleteMeeting,
            variant: 'critical',
          },
          secondary: {
            label: i18n.t('common.cancel'),
            onClick: () => setOpenDeleteDialog(false),
          },
        }}
        header={{
          description: i18n.t('meetings.meeting.edit_meeting.delete_meeting_description'),
          title: i18n.t('meetings.meeting.edit_meeting.delete_meeting'),
          type: 'critical',
        }}
        open={openDeleteDialog && !isLoadingDeleteMeeting}
      />
    </>
  )
}
